import { useState, useCallback } from "react";
import { GetPrescriptionProtocol } from "../..";
import { endpoints } from "../../../../configs/Settings";
import { useOrganizationContext } from "../../../../contexts/OrganizationContext";

interface UseProtocolStatsResult {
  /** Dados dos protocolos */
  data: fhir5.Bundle | undefined;
  /** Estado de carregamento */
  loading: boolean;
  /** Estado de erro */
  error: Error | null;
  /** Função para buscar os dados */
  fetchStats: () => Promise<void>;
}

/**
 * Hook para buscar estatísticas de protocolos
 *
 * @example
 * ```tsx
 * const { data, loading, error, fetchStats } = useProtocolStats();
 *
 * // Buscar dados
 * useEffect(() => {
 *   fetchStats();
 * }, [fetchStats]);
 * ```
 */
export function useProtocolStats(): UseProtocolStatsResult {
  const [data, setData] = useState<fhir5.Bundle>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { organization } = useOrganizationContext();

  const fetchStats = useCallback(async () => {
    if (!organization?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await GetPrescriptionProtocol({
        questionnaire: endpoints.PROTOCOL_ID,
        author: organization.id,
      });

      setData(response);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Erro ao buscar estatísticas de protocolos")
      );
      console.error("useProtocolStats error:", err);
    } finally {
      setLoading(false);
    }
  }, [organization]);

  return {
    data,
    loading,
    error,
    fetchStats,
  };
}
